import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing the banner slightly for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allConsent));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(minimalConsent));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-[#08080C] rounded-2xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00F0FF]/20 to-[#FFE800]/20 px-4 py-3 md:px-6 md:py-4 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-2 bg-[#00F0FF]/20 rounded-lg">
                  <Cookie className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base md:text-lg">Cookie & Privacy Notice</h3>
                  <p className="text-gray-400 text-xs md:text-sm">We value your privacy</p>
                </div>
              </div>
              <button
                onClick={handleRejectAll}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
              <p className="text-gray-300 text-sm md:text-base">
                Pulsate Global uses cookies and similar technologies to enhance your experience, 
                analyze site traffic, and provide personalized content. By continuing to use our platform, 
                you agree to our use of cookies in accordance with our Privacy Policy.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500 mb-4">
              <Globe className="w-4 h-4" />
              <span>GDPR, CCPA, and global privacy compliant</span>
            </div>

            {/* Expandable Details */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-[#0F0F16] rounded-xl p-4 mb-4 space-y-3 border border-white/5">
                    {/* Necessary Cookies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-white">Necessary Cookies</p>
                        <p className="text-xs text-gray-500">Required for the platform to function</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.necessary}
                        disabled
                        className="w-4 h-4 accent-[#00F0FF] rounded cursor-not-allowed"
                      />
                    </div>

                    {/* Functional Cookies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-white">Functional Cookies</p>
                        <p className="text-xs text-gray-500">Remember your preferences and settings</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences(p => ({...p, functional: e.target.checked}))}
                        className="w-4 h-4 accent-[#00F0FF] rounded cursor-pointer"
                      />
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-white">Analytics Cookies</p>
                        <p className="text-xs text-gray-500">Help us improve our services</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(p => ({...p, analytics: e.target.checked}))}
                        className="w-4 h-4 accent-[#00F0FF] rounded cursor-pointer"
                      />
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-white">Marketing Cookies</p>
                        <p className="text-xs text-gray-500">Personalized ads and recommendations</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(p => ({...p, marketing: e.target.checked}))}
                        className="w-4 h-4 accent-[#00F0FF] rounded cursor-pointer"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2.5 md:py-3 bg-[#00F0FF] text-black rounded-xl font-medium hover:bg-[#00F0FF]/90 transition-colors text-sm md:text-base shadow-lg shadow-[#00F0FF]/20"
                data-testid="accept-all-cookies"
              >
                Accept All
              </button>
              
              {showDetails ? (
                <button
                  onClick={handleAcceptSelected}
                  className="flex-1 px-4 py-2.5 md:py-3 bg-[#0F0F16] text-white rounded-xl font-medium hover:bg-[#0F0F16]/80 transition-colors text-sm md:text-base border border-white/10"
                  data-testid="accept-selected-cookies"
                >
                  Save Preferences
                </button>
              ) : (
                <button
                  onClick={() => setShowDetails(true)}
                  className="flex-1 px-4 py-2.5 md:py-3 bg-[#0F0F16] text-white rounded-xl font-medium hover:bg-[#0F0F16]/80 transition-colors text-sm md:text-base border border-white/10"
                  data-testid="customize-cookies"
                >
                  Customize
                </button>
              )}
              
              <button
                onClick={handleRejectAll}
                className="flex-1 px-4 py-2.5 md:py-3 border border-white/20 text-gray-300 rounded-xl font-medium hover:bg-white/5 transition-colors text-sm md:text-base"
                data-testid="reject-all-cookies"
              >
                Reject All
              </button>
            </div>

            {/* Privacy Links */}
            <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-white/10">
              <a href="#privacy" className="text-xs text-[#00F0FF] hover:underline">Privacy Policy</a>
              <a href="#terms" className="text-xs text-[#00F0FF] hover:underline">Terms of Service</a>
              <a href="#cookies" className="text-xs text-[#00F0FF] hover:underline">Cookie Policy</a>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
